import React from 'react';
import { FileText, Trash2, Upload, AlertTriangle, DollarSign, Users } from 'lucide-react';
import { StoredReport } from '../../utils/indexedDB';
import { formatCurrency } from '../../utils/timeUtils';

interface ReportListProps {
  reports: StoredReport[];
  onLoadReport: (report: StoredReport) => void;
  onDeleteReport: (id: string) => void;
}

export function ReportList({ reports, onLoadReport, onDeleteReport }: ReportListProps) {
  if (reports.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
        <p className="text-slate-400">No saved reports</p>
        <p className="text-sm text-slate-500 mt-2">
          Reports are automatically saved when you upload a file
        </p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700">
        <h3 className="text-sm font-medium text-slate-300">
          Saved Reports ({reports.length})
        </h3>
      </div>
      <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
        {reports.map((report) => (
          <div
            key={report.id}
            className="p-4 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <FileText className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-200 truncate">
                    {report.fileName}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(report.uploadDate).toLocaleDateString()} • {report.weekRange}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="flex items-center gap-1 text-green-400">
                      <DollarSign className="w-3 h-3" />
                      {formatCurrency(report.summary.totalRevenue)}
                    </span>
                    <span className="flex items-center gap-1 text-yellow-400">
                      <AlertTriangle className="w-3 h-3" />
                      {report.summary.totalAlerts} alerts
                    </span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <Users className="w-3 h-3" />
                      {report.summary.staffCount} staff
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => onLoadReport(report)}
                  className="px-3 py-1.5 text-xs font-medium text-blue-400 hover:text-blue-300 
                           bg-blue-500/10 hover:bg-blue-500/20 rounded transition-colors"
                >
                  Load
                </button>
                <button
                  onClick={() => onDeleteReport(report.id)}
                  className="p-1.5 text-slate-400 hover:text-red-400 
                           hover:bg-red-500/10 rounded transition-colors"
                  title="Delete report"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
